import {
  isSinglePhaseWorkshopStation,
} from './operatorRoles';
import { userSeesAllProductionLots } from './productionLotVisibility';
import {
  normalizeWorkshopRoleType,
  pickWorkshopPhaseForRole,
  WORKSHOP_OPERATOR_ROLE_LABELS,
} from './workshopOperatorView';
import {
  isLotReadyForStation,
  isWorkshopQueueStationRole,
} from './workshopStationQueue';

/** @typedef {'to_process'|'waiting'|'done'|'excluded'} OperatorLotSegment */
/** @typedef {'active'|'pending'|'urgent_delayed'|'done'|'excluded'} ManagerLotSegment */

const DONE_STATUSES = new Set(['completed', 'quality_check']);
const ACTIVE_STATUSES = new Set(['in_progress', 'quality_check']);

export function isLotUrgent(batch) {
  if (!batch?.deadline) return false;
  const days = Math.ceil(
    (new Date(batch.deadline) - new Date()) / (1000 * 60 * 60 * 24)
  );
  return days <= 2;
}

export function isLotOverdue(batch) {
  if (!batch?.deadline) return false;
  if (batch.status === 'completed') return false;
  return new Date(batch.deadline) < new Date();
}

function getPhases(batch) {
  return batch?.caseTimelinePhases || batch?.case?.case_timeline_phases || [];
}

/** Lot assigné à l’utilisateur (responsable lot ou phase du poste). */
export function isUserAssignedToWorkshopLot(batch, userId, stationRole) {
  if (!userId || !stationRole) return false;
  const workshopPhase = pickWorkshopPhaseForRole(getPhases(batch), stationRole);
  const batchResponsible = batch.responsibleId ?? batch.responsible ?? null;
  if (batchResponsible === userId) return true;
  if (workshopPhase?.responsible_user_id === userId) return true;
  return false;
}

/**
 * Phase atelier du poste pour affichage badge.
 */
export function getWorkshopPhaseForUser(batch, primaryRole) {
  const stationRole = normalizeWorkshopRoleType(primaryRole) || primaryRole;
  if (!stationRole) return null;
  return pickWorkshopPhaseForRole(getPhases(batch), stationRole);
}

export function getStationLabelForRole(roleType) {
  const key = normalizeWorkshopRoleType(roleType) || roleType;
  return WORKSHOP_OPERATOR_ROLE_LABELS[key] || key || 'Production';
}

/**
 * Classification opérateur :
 * - to_process : commande prête pour le poste
 * - waiting : autres lots actifs (pas encore / plus au poste)
 * - done : lots terminés
 *
 * @param {object} batch
 * @param {{ userId?: string, primaryRole?: string, roleTypes?: string[] }} ctx
 * @returns {OperatorLotSegment|ManagerLotSegment}
 */
export function classifyLotForUser(batch, ctx) {
  const { primaryRole, roleTypes = [] } = ctx || {};
  const seesAll = userSeesAllProductionLots(primaryRole, roleTypes);

  if (seesAll) {
    return classifyLotForManager(batch);
  }

  if (DONE_STATUSES.has(batch?.status) || batch?.status === 'completed') {
    return 'done';
  }

  const stationRole = normalizeWorkshopRoleType(primaryRole) || primaryRole;

  if (!isSinglePhaseWorkshopStation(stationRole)) {
    return 'to_process';
  }

  const isQueueStation = isWorkshopQueueStationRole(stationRole);

  if (isQueueStation && isLotReadyForStation(batch, stationRole)) {
    return 'to_process';
  }

  if (!isQueueStation) {
    const workshopPhase = pickWorkshopPhaseForRole(getPhases(batch), stationRole);
    return workshopPhase?.status === 'in_progress' ? 'to_process' : 'waiting';
  }

  return 'waiting';
}

/** @param {object} batch */
export function classifyLotForManager(batch) {
  if (batch?.status === 'completed') return 'done';
  if (batch?.status === 'pending') return 'pending';
  if (
    batch?.status === 'delayed' ||
    isLotUrgent(batch) ||
    isLotOverdue(batch)
  ) {
    return 'urgent_delayed';
  }
  if (ACTIVE_STATUSES.has(batch?.status) || batch?.status === 'packaging') {
    return 'active';
  }
  return 'active';
}

/**
 * @param {object[]} lots
 * @param {string} segment
 * @param {{ userId?: string, primaryRole?: string, roleTypes?: string[] }} ctx
 */
export function filterLotsBySegment(lots, segment, ctx) {
  const list = Array.isArray(lots) ? lots : [];
  const seesAll = userSeesAllProductionLots(ctx?.primaryRole, ctx?.roleTypes);

  return list.filter((b) => {
    const key = seesAll ? classifyLotForManager(b) : classifyLotForUser(b, ctx);
    return key === segment;
  });
}

/**
 * Lots visibles sur le tableau :
 * - responsables / rôles élargis : tous les lots hors brouillon ;
 * - postes de la chaîne : uniquement leur file prête à produire ;
 * - autres postes : comportement historique.
 *
 * `options.includeWholeWorkshop` lève la restriction de poste : l'opérateur
 * cherche alors une commande précise dans tout l'atelier.
 */
export function filterBatchesForTrackingDashboard(batches, ctx, options = {}) {
  const list = Array.isArray(batches) ? batches : [];
  const nonDraft = list.filter((b) => b?.status !== 'draft');
  if (options.includeWholeWorkshop) {
    return nonDraft;
  }
  if (userSeesAllProductionLots(ctx?.primaryRole, ctx?.roleTypes)) {
    return nonDraft;
  }

  const stationRole =
    normalizeWorkshopRoleType(ctx?.primaryRole) || ctx?.primaryRole;
  const isQueueStation = isWorkshopQueueStationRole(stationRole);

  return isQueueStation
    ? nonDraft.filter((batch) => isLotReadyForStation(batch, stationRole))
    : nonDraft;
}

/**
 * @param {object[]} lots
 */
export function sortLotsForQueue(lots) {
  return [...(lots || [])].sort((a, b) => {
    const ua = isLotUrgent(a) ? 0 : 1;
    const ub = isLotUrgent(b) ? 0 : 1;
    if (ua !== ub) return ua - ub;
    const da = a.deadline ? new Date(a.deadline).getTime() : Infinity;
    const db = b.deadline ? new Date(b.deadline).getTime() : Infinity;
    return da - db;
  });
}

/**
 * KPI pour vue responsable.
 */
export function computeManagerKpis(batches) {
  const list = (batches || []).filter((b) => b?.status !== 'draft');
  return {
    active: list.filter((b) => classifyLotForManager(b) === 'active').length,
    pending: list.filter((b) => classifyLotForManager(b) === 'pending').length,
    urgentDelayed: list.filter((b) => classifyLotForManager(b) === 'urgent_delayed').length,
    done: list.filter((b) => classifyLotForManager(b) === 'done').length,
  };
}

/**
 * KPI pour vue opérateur (toutes commandes + focus poste).
 */
export function computeOperatorKpis(batches, ctx) {
  const list = (batches || []).filter((b) => b?.status !== 'draft');
  const stationRole =
    normalizeWorkshopRoleType(ctx?.primaryRole) || ctx?.primaryRole;
  let atStation = 0;
  let otherActive = 0;
  let urgent = 0;
  let done = 0;
  let assignedToMe = 0;

  for (const batch of list) {
    const segment = classifyLotForUser(batch, ctx);
    if (segment === 'done') {
      done += 1;
      continue;
    }
    if (isLotUrgent(batch) || isLotOverdue(batch)) urgent += 1;
    if (segment === 'to_process') atStation += 1;
    else otherActive += 1;
    if (isUserAssignedToWorkshopLot(batch, ctx?.userId, stationRole)) {
      assignedToMe += 1;
    }
  }

  return {
    total: list.length,
    atStation,
    otherActive,
    urgent,
    done,
    assignedToMe,
  };
}
